// File: frontend/src/pages/Plato/components/KnowledgeInput/KnowledgeInput.js

import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { 
  BookOpen, 
  Shield, 
  Network, 
  Sliders, 
  Paperclip, 
  Send, 
  Upload 
} from 'lucide-react';
import styles from './KnowledgeInput.module.css';

const KnowledgeInput = forwardRef(({
  onProcessInput,
  selectedTemplate,
  onTemplateClick,
  selectedDomain,
  onDomainClick,
  onCapabilityClick,
  onParameterClick,
  disabled,
  knowledgeId,
  isInputProcessing
}, ref) => {
  const { t } = useLanguage();
  const [input, setInput] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-resize textarea as content grows
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (input.trim() && !isInputProcessing) {
      onProcessInput(input);
      setInput('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    if (!input.trim()) {
      setIsExpanded(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      try {
        // Process each file
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileContent = await readFileContent(file);
          
          // Append file name and content to input
          const fileInput = `File: ${file.name}\n\n${fileContent}`;
          setInput(prev => prev ? `${prev}\n\n${fileInput}` : fileInput);
        }
      } catch (error) {
        console.error('Error processing files:', error);
      }
    }
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      if (file.type.match('text.*') || 
          file.type === 'application/json' || 
          file.type === 'application/xml') {
        reader.readAsText(file);
      } else if (file.type.match('image.*')) {
        // For images, we might want to handle differently
        resolve(`[Image file: ${file.name}]`);
      } else {
        // For other file types, just acknowledge receipt
        resolve(`[File received: ${file.name} (${file.type})]`);
      }
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      try {
        // Process each file
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileContent = await readFileContent(file);
          
          // Append file name and content to input
          const fileInput = `File: ${file.name}\n\n${fileContent}`;
          setInput(prev => prev ? `${prev}\n\n${fileInput}` : fileInput);
        }
      } catch (error) {
        console.error('Error processing dropped files:', error);
      }
    }
  };

  // Get icon component based on template/domain
  const getTemplateIcon = () => {
    if (selectedTemplate.icon === 'BookOpen') return <BookOpen size={16} />;
    if (selectedTemplate.icon === 'BarChart') return <BookOpen size={16} />;
    if (selectedTemplate.icon === 'Network') return <Network size={16} />;
    return <BookOpen size={16} />;
  };

  const getDomainIcon = () => {
    if (selectedDomain.icon === 'Shield') return <Shield size={16} />;
    if (selectedDomain.icon === 'DollarSign') return <Shield size={16} />;
    if (selectedDomain.icon === 'Heart') return <Shield size={16} />;
    if (selectedDomain.icon === 'Scale') return <Shield size={16} />;
    if (selectedDomain.icon === 'Cpu') return <Shield size={16} />;
    return <Shield size={16} />;
  };

  return (
    <div 
      className={`${styles.inputContainer} ${isExpanded ? styles.expanded : ''}`}
      ref={ref}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={styles.selectionBar}>
        <button 
          className={styles.templateButton}
          onClick={onTemplateClick}
          disabled={disabled || isInputProcessing}
        >
          <span className={styles.templateIcon}>
            {getTemplateIcon()}
          </span>
          <span className={styles.templateName}>{selectedTemplate.name}</span>
        </button>
        
        <button 
          className={styles.domainButton}
          onClick={onDomainClick}
          disabled={disabled || isInputProcessing}
        >
          <span className={styles.domainIcon}>
            {getDomainIcon()}
          </span>
          <span className={styles.domainName}>{selectedDomain.name}</span>
        </button>
        
        <button 
          className={styles.capabilityButton}
          onClick={onCapabilityClick}
          disabled={disabled || isInputProcessing}
        >
          <span className={styles.capabilityIcon}>
            <Network size={16} />
          </span>
          <span className={styles.capabilityName}>{t('plato.capability_matrix')}</span>
        </button>
        
        <button 
          className={styles.parameterButton}
          onClick={onParameterClick}
          disabled={disabled || isInputProcessing}
        >
          <span className={styles.parameterIcon}>
            <Sliders size={16} />
          </span>
          <span className={styles.parameterName}>{t('plato.parameters')}</span>
        </button>
      </div>
      
      <div className={`${styles.inputWrapper} ${isDragActive ? styles.dragActive : ''}`}>
        <textarea
          ref={textareaRef}
          className={styles.inputField}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={t('plato.input_placeholder')}
          disabled={disabled || isInputProcessing}
          rows={1}
        />
        
        <div className={styles.inputActions}>
          <button
            className={styles.attachButton}
            onClick={handleFileSelect}
            disabled={disabled || isInputProcessing}
            title={t('plato.attach_file')}
          >
            <Paperclip size={18} />
          </button>
          
          <button
            className={`${styles.sendButton} ${!input.trim() || isInputProcessing ? styles.disabled : ''}`}
            onClick={handleSubmit}
            disabled={!input.trim() || disabled || isInputProcessing}
          >
            {isInputProcessing ? (
              <div className={styles.processingIndicator}>
                <div className={styles.processingSpinner}></div>
              </div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>
      
      <input
        type="file"
        ref={fileInputRef}
        className={styles.fileInput}
        onChange={handleFileChange}
        multiple
      />
      
      {isDragActive && (
        <div className={styles.dropOverlay}>
          <div className={styles.dropMessage}>
            <Upload size={48} />
            <p>{t('plato.drop_files')}</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default KnowledgeInput;